import React, { Suspense, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { IEvent, IEventLeague } from '../../api/dto/IEvent'
import { ILink, links } from '../../assets/constants/linksItems'
import { filterFunc, parseAndFilterFunk } from '../../assets/constants/favouritesInStorage'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { eventRepository } from '../../api/eventRepository'
import { LoadMoreIcon } from '../../assets/icons/loadMoreIcon'
import { EventHeader } from '../../components/eventHeader/eventHeader'
import { useQuery } from '../../assets/constants/useQueryFunc'
import type { IEventWithStream } from '../../App'
import { InitialStateContext } from '../../App'
import { CloseIcon } from '../../assets/icons/closeIcon'
import { TableItemPlug } from '../../ui/plugs/tableItemPlug'
import { TrailBlock } from '../../components/trailBlock/trailBlock'
import Layout from '../../layout/layout'
import { IStream } from '../../api/dto/IStream'
import { StreamBlock } from '../../components/streamBlock/streamBlock'
import ChannelsItem from '../../components/channelsItem/channelsItem'
import { EventsSidebar } from '../../components/EventsSidebar'
import { Footer } from '../../layout/components/Footer'
import { useSportTrial } from '../../hooks/useSportTrial'
import {
  CHANNELS_LEAGUE_IDS,
  EXCLUDE_FROM_CHANNEL_NAME,
  STREAM_FILTER_PARAM,
} from '../../constant/stream'
import useProfileQuery from '../../queries/use-profile-query'
import { trackEvent } from '../../utils/analytics'
import { SPORTS } from '../../constant/sports'
import PricingModal from '../../layout/components/header/components/pricing/pricingModal'
import PricingSection from '../../components/pricingSection/pricingSection'

const TvPage = () => {
  const { setModal, currentSport, setCurrentSport } = useContext(InitialStateContext)

  const { token: isLoggedIn, isValidSubscription, isActiveSubscription } = useProfileQuery()
  const query = useQuery()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const firstRenderRef = useRef(true)
  const currentQueryParams = Object.fromEntries(searchParams.entries())

  const [allEvents, setAllEvents] = useState<IEvent[]>([])
  const [loading, setLoading] = useState(false)
  const [loadingStream, setLoadingStream] = useState(false)
  const [loadingEvent, setLoadingEvent] = useState(false)
  const [currentEvent, setCurrentEvent] = useState<IEventWithStream | null>(null)
  const [streams, setStreams] = useState<IStream[]>([])
  const [trialContext, setTrialContext] = useState({
    currentSport,
    countDown: false,
  })
  const [sportId, setSportId] = useState<string>('')

  //   const [isOpen, setIsOpen] = useState(true)
  //   const closeModal = () => setIsOpen(false)

  const trial = useSportTrial(trialContext.currentSport, trialContext.countDown)

  const defaultChannel = useMemo(
    () => (currentSport !== SPORTS.CHANNELS ? streams[0] : null),
    [streams, currentSport]
  )

  const showStream = useMemo(() => {
    const notUthorizedAndNotSubscribed = !isValidSubscription && !isActiveSubscription
    const authorizedAndSubscribed = isValidSubscription && isActiveSubscription
    if (trial.isActive) {
      if (notUthorizedAndNotSubscribed || authorizedAndSubscribed) {
        return true
      }
      if (isValidSubscription && !isActiveSubscription) {
        return false
      }
    } else if (authorizedAndSubscribed) {
      return true
    }
    return false
  }, [trial.isActive, isValidSubscription, isActiveSubscription])

  const queryName = query.get('name')
  const queryLeague = query.get('league') as SPORTS
  const querySeries = query.get('series')

  const prepareStreams = (res: IStream[]) =>
    res
      .filter((item) => item.description === STREAM_FILTER_PARAM)
      .map((item) => ({
        ...item,
        name: item.name.replace(EXCLUDE_FROM_CHANNEL_NAME, ''),
      }))

  const setEventFunk = async (event: IEvent) => {
    try {
      setLoadingEvent(true)
      const res = await eventRepository.fetchStreamsByMatchClient(event.uuid)
      const allStreams = res
        ?.filter((filter) => !Array.isArray(filter?.collection))
        .map((el) => Object.values(el.collection))
        .flat(2)
      const streams = prepareStreams(allStreams)
      setCurrentEvent({
        event,
        stream: streams.length ? streams : undefined,
      })
    } catch (error) {
      console.error(error)
    } finally {
      setLoadingEvent(false)
    }
  }

  useEffect(() => {
    const interval = setInterval(() => {
      const someFunc = (filterEvents: IEvent[]) =>
        filterEvents.some((some) => new Date(some.end_time * 1000).getTime() < Date.now())
      if (allEvents?.length && someFunc(allEvents)) {
        setAllEvents((prevState) => filterFunc(prevState))
      }
    }, 120000)

    return () => clearInterval(interval)
  }, [allEvents])

  const getSportEventsStreams = async (
    sportItem: ILink,
    withDefaultStream = false,
    signal?: AbortSignal
  ) => {
    const { link } = sportItem
    if (!link) return
    setLoading(true)

    try {
      const [eventsRes, streamsRes] = await Promise.allSettled([
        eventRepository.fetchAllEventsClient(
          {
            [link?.key]: link.value,
          },
          signal
        ),
        eventRepository.fetchStreamsByLeague(sportItem.leagueId || '', signal),
      ])
      if (streamsRes.status === 'fulfilled') {
        const streams = prepareStreams(streamsRes?.value || [])
        setStreams(streams)
        if (withDefaultStream) {
          setCurrentEvent({ stream: streams })
          setSportId(sportItem.link?.value || '')
        }
      }
      if (eventsRes.status === 'fulfilled') {
        const allEvents = parseAndFilterFunk(eventsRes.value)
        setAllEvents(allEvents)
        if (queryName) {
          const match = allEvents.find(
            (el) => el.participantHome.name.toLowerCase().replace(/ /g, '_') === queryName
          )
          if (match) {
            setEventFunk(match)
          }
        }
      }
    } catch (error) {
      console.error(error)
    } finally {
      setLoading(false)
    }
  }

  const getChannelsStreams = async (withDefaultStream = false, signal?: AbortSignal) => {
    setLoadingStream(true)
    try {
      const fistRes = await eventRepository.fetchStreamsByLeague(CHANNELS_LEAGUE_IDS[0], signal)
      const firstStreams = prepareStreams(fistRes || [])

      setStreams(firstStreams)

      // If `withDefaultStream` is true, set the first stream as the default
      withDefaultStream && setCurrentEvent({ stream: [firstStreams[0]] })

      setLoadingStream(false)

      const secondRes = await eventRepository.fetchStreamsByLeague(CHANNELS_LEAGUE_IDS[1], signal)
      const secondStreams = prepareStreams(secondRes || [])
      setStreams([...firstStreams, ...secondStreams])
    } catch (error) {
      console.error(error)
    } finally {
      setLoadingStream(false)
    }
  }

  const getStreamsBySport = async (
    name: SPORTS,
    withDefaultStream = false,
    isNewSport = false,
    signal?: AbortSignal
  ) => {
    const isChannels = name === SPORTS.CHANNELS
    const newParams = {
      ...(!isNewSport && { ...currentQueryParams }),
      ...(!isChannels && { league: name }),
    }

    const navigateParams = isChannels
      ? {}
      : { search: '?' + new URLSearchParams(newParams).toString() }

    navigate(navigateParams)
    const sportParams = links.find((el) => el.name === name)
    const sportHasLink = !!sportParams?.link
    if (sportHasLink) {
      await getSportEventsStreams(sportParams, withDefaultStream, signal)
    } else {
      await getChannelsStreams(withDefaultStream, signal)
    }
  }

  const matchStateSportToQuery = (querySport: SPORTS) => {
    //TODO Could be avoided by setting the currentSport to the querySport on first render
    const isValidQuerySport = Object.values(SPORTS).includes(querySport)
    const isSameSport = isValidQuerySport && querySport === currentSport
    const isMatchable = isValidQuerySport && !isSameSport
    if (firstRenderRef.current) {
      firstRenderRef.current = false
      isMatchable && setCurrentSport(querySport)
      return !isMatchable
    }
    return true
  }
  useEffect(() => {
    const controller = new AbortController()
    const signal = controller.signal

    const isDesktop = window.innerWidth > 920
    const isQuerySportMatching = matchStateSportToQuery(queryLeague)
    if (!isQuerySportMatching) {
      return
    }
    const isNewSport = currentSport !== queryLeague
    const presetDefaultStream = !currentEvent && isDesktop
    getStreamsBySport(currentSport, presetDefaultStream, isNewSport, signal)

    return () => {
      controller.abort()
    }
  }, [currentSport])

  useEffect(() => {
    const hasOneActiveStream = Boolean(
      currentEvent?.stream?.some((el) => el?.stream?.includes('iframe'))
    )
    setTrialContext({
      currentSport: hasOneActiveStream ? currentSport : SPORTS.CHANNELS,
      countDown: hasOneActiveStream,
    })
  }, [currentEvent])

  const handleSetCurrentEvent = (item: IEventWithStream, isRefresh?: boolean) => {
    if (
      item.stream?.[0].uuid &&
      currentEvent?.stream?.[0].uuid === item.stream?.[0].uuid &&
      !isRefresh
    ) {
      return
    }
    trackEvent({
      category: 'event',
      action: 'click',
      label: item.event?.title,
    })
    if (!showStream) {
      setModal('custom')
    }
    const newParams = {
      ...currentQueryParams,
      name: item.event?.participantHome.name?.replace(/ /g, '_')?.toLowerCase() || '',
    }

    navigate({ search: '?' + new URLSearchParams(newParams).toString() })

    if (item.event) {
      setEventFunk(item.event)
    }

    const sportParams = links.find((el) => el.name === item.event?.sport?.name)
    setSportId(sportParams?.link?.value || '')
  }

  const handleClickStream = (item: IStream) => {
    trackEvent({
      category: item.type,
      action: 'click',
      label: item.name,
    })
    if (!showStream) {
      setModal('custom')
    }

    if (queryLeague) {
      navigate({
        search:
          '?' +
          new URLSearchParams({
            league: `${queryLeague}`,
          }).toString(),
      })
      const sportParams = links.find((el) => el.name === queryLeague)
      setSportId(sportParams?.link?.value || '')
    } else {
      setSportId('')
    }

    setCurrentEvent({ stream: [item] })
  }

  const handleLeagueChange = (league: IEventLeague) => {
    if (querySeries === league.uuid) return
    const newParams = {
      ...currentQueryParams,
      series: league.uuid,
    }
    navigate({ search: '?' + new URLSearchParams(newParams).toString() })
  }

  const event = loadingEvent ? (
    <div className="w-full h-full flex items-center justify-center">
      <LoadMoreIcon className="h-[30px] sm:h-[50px] w-[30px] sm:w-[50px] stroke-blue loader" />
    </div>
  ) : currentEvent?.stream ? (
    currentEvent?.stream?.[0]?.stream?.includes('iframe') ? (
      <StreamBlock
        stream={currentEvent?.stream}
        trialInfo={trial}
        isLoggedIn={isValidSubscription}
        sportId={sportId}
      />
    ) : currentEvent?.stream?.length ? (
      <span className="w-full h-full flex items-center justify-center bg-black text-white font-rubikBold text-sm unique:text-lg py-1 unique:py-2 px-2 unique:px-4 rounded">
        This Channel is coming soon
      </span>
    ) : (
      <span className="w-full h-full flex items-center justify-center bg-black text-white font-rubikBold text-sm unique:text-lg py-1 unique:py-2 px-2 unique:px-4 rounded">
        There are no events to display
      </span>
    )
  ) : (
    currentEvent && (
      <EventHeader
        item={currentEvent.event}
        onClick={() => handleSetCurrentEvent(currentEvent, true)}
      />
    )
  )

  return (
    <Layout currentSport={currentSport}>
      {currentEvent ? (
        <>
          <div
            onClick={() => {
              setCurrentEvent(null)
            }}
            className="fixed top-0 left-0 w-full h-full bg-blackOpacity z-[100] unique:hidden px-2 flex items-center justify-center"
          >
            <div
              onClick={(e) => e.stopPropagation()}
              className={`relative rounded-lg  ${showStream ? 'h-[225px] w-[90vw]' : 'overflow-scroll h-full py-24 w-full'}`}
            >
              <div>
                <div
                  onClick={() => {
                    setCurrentEvent(null)
                  }}
                  className="cursor-pointer bg-darkBlue w-[40px] absolute top-5 right-5 h-[40px] flex items-center justify-center rounded-lg"
                >
                  <CloseIcon className="stroke-white h-[30px] w-[30px]" />
                </div>
                {/* {showStream ? event : <TrailBlock />} */}
                {showStream ? event : <PricingSection isLoggedIn={!!isLoggedIn} />}
              </div>
            </div>
          </div>
          {/* <PricingModal isVisible={isOpen} closeModal={closeModal} /> */}
        </>
      ) : null}
      <div className="flex justify-between flex-grow-[1]">
        <div className="flex flex-col w-full h-full unique:w-[425px] unique:mr-[15px] rounded-[16px] bg-whiteOpacity1 min-h-[550px]">
          {currentSport !== 'Channels' ? (
            loading ? (
              <div className="max-h-full unique:min-h-max unique:pr-2">
                {new Array(2).fill(null).map((_, i) => (
                  <TableItemPlug key={i} />
                ))}
              </div>
            ) : (
              <div className="max-h-full h-full unique:h-[82vh] unique:min-h-full">
                <EventsSidebar
                  initialLeagueId={querySeries}
                  allEvents={allEvents}
                  currentEvent={currentEvent}
                  setCurrentEvent={handleSetCurrentEvent}
                  onLeagueChange={handleLeagueChange}
                  defaultChannel={defaultChannel}
                  onStreamClick={handleClickStream}
                />
              </div>
            )
          ) : !loadingStream ? (
            <div className="max-h-full h-full unique:h-[82vh]  unique:min-h-full overflow-y-hidden">
              <div className="flex flex-col h-full thinScrollbar overflow-auto m-2">
                <Suspense>
                  {streams?.length ? (
                    streams.map((el) => (
                      <ChannelsItem
                        onClick={() => handleClickStream(el)}
                        key={el.uuid}
                        item={el}
                        currentEvent={currentEvent}
                      />
                    ))
                  ) : (
                    <span className="w-full h-full flex items-start justify-center  text-white font-rubikBold text-sm unique:text-lg py-1 unique:py-2 px-2 unique:px-4 rounded">
                      There are no channels to display
                    </span>
                  )}
                </Suspense>
              </div>
            </div>
          ) : null}
        </div>
        <div className="hidden overflow-hidden unique:flex w-[calc(100%-265px)] header:w-[calc(100%-325px)] flex-col justify-between gap-[24px]">
          <div className={`w-full rounded-lg ${showStream ? 'min-h-[550px]' : 'h-full'}`}>
            {/* {showStream ? event : <TrailBlock />} */}
            {showStream ? event : <PricingSection isLoggedIn={!!isLoggedIn} />}
          </div>
          <Footer className="bg-whiteOpacity1" />
        </div>

        {/* {!showStream && <PricingModal isVisible={isOpen} closeModal={closeModal} />} */}
      </div>
    </Layout>
  )
}

export default TvPage
